import { useState, useEffect } from 'react';
import { dischargeAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Plus, FileText, CheckCircle, Clock, Printer, Eye } from 'lucide-react';
import { cn } from '../lib/utils';

const DischargeSummaryPage = () => {
  const { hospital } = useAuth();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      // Load all discharge summaries — we'll use a generic search
      const res = await dischargeAPI.getByPatient('all');
      setSummaries(res.data || []);
    } catch {
      // If 'all' doesn't work, use empty array
      setSummaries([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = summaries.filter(s =>
    s.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.diagnosis?.toLowerCase().includes(search.toLowerCase())
  );

  const handleFinalize = async (id) => {
    try {
      await dischargeAPI.finalize(id);
      toast.success('Discharge summary finalized');
      load();
    } catch { toast.error('Failed to finalize'); }
  };

  return (
    <div className="space-y-5" data-testid="discharge-summary-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Discharge Summaries</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage patient discharge documentation</p>
        </div>
        <Button onClick={() => { setEditItem(null); setShowModal(true); }} className="bg-teal-600 hover:bg-teal-700" data-testid="new-discharge-btn">
          <Plus size={16} className="mr-1.5" /> New Summary
        </Button>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by patient name or diagnosis..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10" data-testid="search-discharge" />
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <FileText size={40} className="mx-auto mb-3 opacity-30" />
            <p>No discharge summaries found</p>
            <p className="text-xs mt-1">Create one using the button above</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(s => (
              <div key={s.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 transition-all"
                data-testid={`discharge-${s.id}`}>
                <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center',
                  s.status === 'finalized' ? 'bg-emerald-100' : 'bg-amber-100')}>
                  {s.status === 'finalized' ? <CheckCircle size={18} className="text-emerald-600" /> : <Clock size={18} className="text-amber-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">{s.patient_name || 'Unknown Patient'}</p>
                  <p className="text-xs text-slate-500 truncate">{s.diagnosis || 'No diagnosis'} &middot; Discharged: {s.discharge_date}</p>
                </div>
                <div className="flex-shrink-0 text-xs text-slate-400">
                  Dr. {s.doctor_name || 'N/A'}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-semibold',
                    s.status === 'finalized' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
                    {s.status === 'finalized' ? 'Finalized' : 'Draft'}
                  </span>
                  <button onClick={() => setViewItem(s)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid={`view-discharge-${s.id}`}><Eye size={14} className="text-slate-400" /></button>
                  <button onClick={() => window.open(`/discharge/print/${s.id}`, '_blank')} className="p-1.5 rounded-lg hover:bg-emerald-50" title="Print" data-testid={`print-discharge-${s.id}`}><Printer size={14} className="text-emerald-600" /></button>
                  {s.status !== 'finalized' && (
                    <>
                      <button onClick={() => { setEditItem(s); setShowModal(true); }} className="p-1.5 rounded-lg hover:bg-slate-100"><FileText size={14} className="text-blue-500" /></button>
                      <button onClick={() => handleFinalize(s.id)} className="p-1.5 rounded-lg hover:bg-emerald-50"><CheckCircle size={14} className="text-emerald-500" /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DischargeModal open={showModal} onClose={() => setShowModal(false)} editItem={editItem} hospitalId={hospital?.id} onSaved={load} />
      <ViewModal open={!!viewItem} onClose={() => setViewItem(null)} item={viewItem} hospitalName={hospital?.name} />
    </div>
  );
};

const DischargeModal = ({ open, onClose, editItem, hospitalId, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editItem) setForm({ ...editItem });
    else setForm({
      patient_name: '', age: '', gender: 'Male', admission_date: '', discharge_date: new Date().toISOString().split('T')[0],
      diagnosis: '', treatment_given: '', procedures_performed: '', follow_up_instructions: '', follow_up_date: '',
      doctor_name: '', condition_at_discharge: 'Stable', diet_instructions: '', activity_restrictions: '', emergency_instructions: '',
      medications_on_discharge: [],
    });
  }, [editItem, open]);

  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.patient_name) { toast.error('Patient name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, hospital_id: hospitalId };
      if (editItem) await dischargeAPI.update(editItem.id, payload);
      else await dischargeAPI.create(payload);
      toast.success(editItem ? 'Updated' : 'Created');
      onSaved(); onClose();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editItem ? 'Edit Discharge Summary' : 'New Discharge Summary'}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Patient Name *</Label><Input value={form.patient_name || ''} onChange={e => up('patient_name', e.target.value)} data-testid="discharge-patient-name" /></div>
            <div><Label>Age</Label><Input value={form.age || ''} onChange={e => up('age', e.target.value)} /></div>
            <div><Label>Gender</Label>
              <Select value={form.gender || 'Male'} onValueChange={v => up('gender', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select></div>
            <div><Label>Admission Date</Label><Input type="date" value={form.admission_date || ''} onChange={e => up('admission_date', e.target.value)} /></div>
            <div><Label>Discharge Date</Label><Input type="date" value={form.discharge_date || ''} onChange={e => up('discharge_date', e.target.value)} /></div>
          </div>
          <div><Label>Diagnosis *</Label><Input value={form.diagnosis || ''} onChange={e => up('diagnosis', e.target.value)} placeholder="Primary diagnosis" data-testid="discharge-diagnosis" /></div>
          <div><Label>Treatment Given</Label><textarea className="w-full rounded-lg border border-slate-200 p-2.5 text-sm" rows={3} value={form.treatment_given || ''} onChange={e => up('treatment_given', e.target.value)} placeholder="Detailed treatment..." /></div>
          <div><Label>Procedures Performed</Label><Input value={form.procedures_performed || ''} onChange={e => up('procedures_performed', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Doctor Name</Label><Input value={form.doctor_name || ''} onChange={e => up('doctor_name', e.target.value)} /></div>
            <div><Label>Condition at Discharge</Label>
              <Select value={form.condition_at_discharge || 'Stable'} onValueChange={v => up('condition_at_discharge', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Stable">Stable</SelectItem><SelectItem value="Improved">Improved</SelectItem><SelectItem value="Unchanged">Unchanged</SelectItem><SelectItem value="Critical">Critical</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div><Label>Follow-up Instructions</Label><textarea className="w-full rounded-lg border border-slate-200 p-2.5 text-sm" rows={2} value={form.follow_up_instructions || ''} onChange={e => up('follow_up_instructions', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Follow-up Date</Label><Input type="date" value={form.follow_up_date || ''} onChange={e => up('follow_up_date', e.target.value)} /></div>
            <div><Label>Diet Instructions</Label><Input value={form.diet_instructions || ''} onChange={e => up('diet_instructions', e.target.value)} /></div>
          </div>
          <div><Label>Activity Restrictions</Label><Input value={form.activity_restrictions || ''} onChange={e => up('activity_restrictions', e.target.value)} /></div>
          <div><Label>Emergency Instructions</Label><Input value={form.emergency_instructions || ''} onChange={e => up('emergency_instructions', e.target.value)} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-discharge-btn">
              {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ViewModal = ({ open, onClose, item, hospitalName }) => {
  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="space-y-4">
          <div className="text-center border-b border-slate-200 pb-4">
            <h2 className="text-lg font-bold text-slate-900">{hospitalName || 'Hospital'}</h2>
            <p className="text-sm text-slate-500">Discharge Summary</p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><span className="text-slate-500">Patient:</span> <span className="font-semibold">{item.patient_name}</span></div>
            <div><span className="text-slate-500">Age/Gender:</span> <span className="font-semibold">{item.age} / {item.gender}</span></div>
            <div><span className="text-slate-500">Doctor:</span> <span className="font-semibold">{item.doctor_name}</span></div>
            <div><span className="text-slate-500">Admitted:</span> <span className="font-semibold">{item.admission_date}</span></div>
            <div><span className="text-slate-500">Discharged:</span> <span className="font-semibold">{item.discharge_date}</span></div>
            <div><span className="text-slate-500">Condition:</span> <span className="font-semibold">{item.condition_at_discharge}</span></div>
          </div>
          {item.diagnosis && <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 font-semibold uppercase mb-1">Diagnosis</p><p className="text-sm">{item.diagnosis}</p></div>}
          {item.treatment_given && <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 font-semibold uppercase mb-1">Treatment Given</p><p className="text-sm whitespace-pre-wrap">{item.treatment_given}</p></div>}
          {item.procedures_performed && <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500 font-semibold uppercase mb-1">Procedures</p><p className="text-sm">{item.procedures_performed}</p></div>}
          {item.follow_up_instructions && <div className="bg-blue-50 rounded-lg p-3"><p className="text-xs text-blue-600 font-semibold uppercase mb-1">Follow-up Instructions</p><p className="text-sm">{item.follow_up_instructions}</p>{item.follow_up_date && <p className="text-xs text-blue-500 mt-1">Next visit: {item.follow_up_date}</p>}</div>}
          {item.diet_instructions && <div><p className="text-xs text-slate-500 font-semibold">Diet:</p><p className="text-sm">{item.diet_instructions}</p></div>}
          {item.activity_restrictions && <div><p className="text-xs text-slate-500 font-semibold">Activity Restrictions:</p><p className="text-sm">{item.activity_restrictions}</p></div>}
          {item.emergency_instructions && <div className="bg-red-50 rounded-lg p-3"><p className="text-xs text-red-600 font-semibold uppercase mb-1">Emergency Instructions</p><p className="text-sm">{item.emergency_instructions}</p></div>}
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => window.print()}>
              <Printer size={14} className="mr-1.5" /> Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DischargeSummaryPage;
